import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, User } from "lucide-react";
import { MicButton } from "@/components/MicButton";
import { AiAssistButton } from "@/components/AiAssistButton";

const IntakeWizard = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [schema, setSchema] = useState(null);
    const [project, setProject] = useState(null);
    const [answers, setAnswers] = useState({});
    const [step, setStep] = useState(0);
    const [wantsTier2, setWantsTier2] = useState(null); // null | true | false
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [avatars, setAvatars] = useState([]);
    const [offers, setOffers] = useState([]);
    const [activeAvatarId, setActiveAvatarId] = useState("");
    const [activeOfferId, setActiveOfferId] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const [{ data: proj }, { data: avs }, { data: ofs }, { data: cp }] = await Promise.all([
                    api.get(`/projects/${id}`),
                    api.get("/me/avatars"),
                    api.get("/me/offers"),
                    api.get("/me/creator-profile"),
                ]);
                setProject(proj);
                setAvatars(avs || []);
                setOffers(ofs || []);
                // Schema decision: if creator profile has name_title, we're good with the base 8Q
                // wizard. Otherwise use the expanded no-avatar schema to gather inline context.
                const hasCreatorProfile = !!(cp?.fields?.name_title);
                const schemaEndpoint = hasCreatorProfile ? "/intake/schema" : "/intake/schema-noavatar";
                const { data: sch } = await api.get(schemaEndpoint);
                setSchema(sch);
                const existingIntake = proj.intake || {};
                setAnswers(existingIntake);
                // Sync active selections from persisted intake
                if (existingIntake.avatar_id) setActiveAvatarId(existingIntake.avatar_id);
                if (existingIntake.offer_id) setActiveOfferId(existingIntake.offer_id);
                // If tier2 already has any values, assume yes
                if ((sch.tier2 || []).some((q) => existingIntake[q.key])) {
                    setWantsTier2(true);
                }
                // Auto-link default Avatar (if any). Avatar supplies the CREATOR
                // background — we do NOT prefill per-video fields.
                const perVideoKeys = new Set([
                    "video_purpose", "video_platforms", "video_topic", "video_pov",
                    "video_myth", "video_data", "video_story", "video_belief_shift",
                    "duration",
                ]);
                const hasAnyPerVideoAnswer = Object.entries(existingIntake).some(
                    ([k, v]) => perVideoKeys.has(k) && v && (Array.isArray(v) ? v.length : String(v).trim())
                );
                if (!hasAnyPerVideoAnswer && (avs || []).length > 0) {
                    const def = avs.find((a) => a.is_default) || avs[0];
                    setActiveAvatarId(def.id);
                    // Prefill desired_cta from Avatar's default CTA as a starting point
                    const cta = def.fields?.desired_cta;
                    const patch = { avatar_id: def.id, ...(cta ? { desired_cta: cta } : {}) };
                    const merged = { ...existingIntake, ...patch };
                    setAnswers(merged);
                    try {
                        await api.patch(`/projects/${id}`, { intake: merged });
                    } catch (e) { /* noop */ }
                }
            } catch (e) {
                toast.error("Failed to load intake");
                navigate("/dashboard");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, navigate]);

    const applyAvatar = async (avatarId) => {
        setActiveAvatarId(avatarId);
        if (!avatarId) return;
        const av = avatars.find((a) => a.id === avatarId);
        if (!av) return;
        // Only merge in the Avatar's CTA (as a starting point) — everything else on
        // the Avatar is background context that lives outside intake now.
        const cta = av.fields?.desired_cta;
        const patch = { avatar_id: avatarId, ...(cta && !answers.desired_cta ? { desired_cta: cta } : {}) };
        const merged = { ...answers, ...patch };
        setAnswers(merged);
        try {
            await api.patch(`/projects/${id}`, { intake: merged });
            toast.success(`Using Avatar "${av.name}"`);
        } catch {
            toast.error("Save failed");
        }
    };

    // Build ordered step list: pre-intake, tier1, optional gate + tier2, review
    const stepPlan = useMemo(() => {
        if (!schema) return [];
        const plan = [];
        schema.pre_intake.forEach((q) => plan.push({ kind: "q", tier: "pre", q }));
        schema.tier1.forEach((q) => plan.push({ kind: "q", tier: "t1", q }));
        const hasTier2 = (schema.tier2 || []).length > 0;
        if (hasTier2) {
            plan.push({ kind: "gate", tier: "gate" });
            if (wantsTier2) {
                schema.tier2.forEach((q) => plan.push({ kind: "q", tier: "t2", q }));
            }
        }
        plan.push({ kind: "review", tier: "review" });
        return plan;
    }, [schema, wantsTier2]);

    const current = stepPlan[step];
    const totalSteps = stepPlan.length;
    const progressPct = totalSteps ? Math.min(100, Math.round((step / (totalSteps - 1)) * 100)) : 0;

    useEffect(() => {
        // Autofocus input when step changes
        setTimeout(() => inputRef.current?.focus?.(), 50);
    }, [step]);

    const persist = async (partial) => {
        setSaving(true);
        try {
            const merged = { ...answers, ...partial };
            setAnswers(merged);
            await api.patch(`/projects/${id}`, { intake: merged });
        } catch (e) {
            toast.error("Auto-save failed");
        } finally {
            setSaving(false);
        }
    };

    const canProceed = () => {
        if (!current) return false;
        if (current.kind === "q") {
            const val = answers[current.q.key];
            if (current.q.required !== false) {
                if (current.q.type === "duration") return !!val;
                if (current.q.type === "multi_choice") return Array.isArray(val) && val.length > 0;
                return !!(val && String(val).trim());
            }
            return true;
        }
        if (current.kind === "gate") return wantsTier2 !== null;
        return true;
    };

    const next = async () => {
        if (!canProceed()) {
            toast.error("Please answer this question to continue");
            return;
        }
        if (current.kind === "q") {
            // Ensure saved
            await persist({ [current.q.key]: answers[current.q.key] });
        }
        setStep((s) => Math.min(totalSteps - 1, s + 1));
    };

    const back = () => setStep((s) => Math.max(0, s - 1));

    const doGenerate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/scripts/generate", { project_id: id });
            const first = data.scripts?.[0];
            const count = data.scripts?.length || 0;
            if (first) {
                toast.success(`${count} script variants generated`);
                navigate(`/scripts/${first.id}`);
            } else {
                toast.error("No variants returned");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Generation failed");
        } finally {
            setGenerating(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="intake-loading">
                <Loader2 className="w-6 h-6 animate-spin text-un-berry" />
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 md:py-10" data-testid="intake-wizard">
            {/* Top bar */}
            <div className="flex items-center justify-between gap-4 mb-6">
                <button
                    onClick={() => navigate(`/projects/${id}`)}
                    className="un-btn-ghost flex items-center gap-2 text-sm"
                    data-testid="intake-back-to-project"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to project
                </button>
                <div className="un-mono text-xs text-un-onyx/60" data-testid="intake-progress-label">
                    Step {step + 1} of {totalSteps} · {progressPct}%
                </div>
            </div>

            <div className="un-progress-track mb-4" data-testid="intake-progress-bar">
                <div className="un-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>

            {/* Video setup — Avatar (audience) + Offer (what this promotes) */}
            <div className="mb-8 p-5 border-2 border-un-onyx bg-un-lime/30 space-y-4" data-testid="intake-video-setup">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="un-mono text-xs font-bold text-un-onyx flex items-center gap-2">
                        <User className="w-4 h-4" /> VIDEO SETUP
                    </div>
                    <Link
                        to={`/profile?return=/projects/${id}/intake`}
                        className="un-mono text-xs underline hover:text-un-berry"
                        data-testid="intake-manage-profile-link"
                    >
                        Manage Profile & Library →
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <div className="un-label text-un-grape mb-2">Speaking To (Avatar)</div>
                        <select
                            value={activeAvatarId}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === "__create__") {
                                    navigate(`/profile?return=/projects/${id}/intake#avatars`);
                                    return;
                                }
                                applyAvatar(v);
                            }}
                            className="un-input !py-2 !px-3 text-sm bg-white w-full"
                            data-testid="intake-avatar-select"
                        >
                            <option value="">-- Skip: describe inline --</option>
                            {avatars.map((a) => (
                                <option key={a.id} value={a.id}>
                                    {a.name}{a.fields?.avatar_type ? ` (${a.fields.avatar_type})` : ""}{a.is_default ? " · Default" : ""}
                                </option>
                            ))}
                            <option value="__create__">+ Create new Avatar…</option>
                        </select>
                    </div>
                    <div>
                        <div className="un-label text-un-grape mb-2">Promoting Offer (optional)</div>
                        <select
                            value={activeOfferId}
                            onChange={async (e) => {
                                const v = e.target.value;
                                if (v === "__create__") {
                                    navigate(`/profile?return=/projects/${id}/intake#offers`);
                                    return;
                                }
                                setActiveOfferId(v);
                                const merged = { ...answers, offer_id: v || null };
                                setAnswers(merged);
                                try { await api.patch(`/projects/${id}`, { intake: merged }); } catch { /* noop */ }
                            }}
                            className="un-input !py-2 !px-3 text-sm bg-white w-full"
                            data-testid="intake-offer-select"
                        >
                            <option value="">-- Authority-only (no offer) --</option>
                            {offers.map((o) => (
                                <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                            <option value="__create__">+ Create new Offer…</option>
                        </select>
                    </div>
                </div>
                <div className="text-xs text-un-onyx/70">
                    Avatar = who this video speaks to. Offer = what this video is promoting (leave blank for authority-building content). Both can be edited any time.
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-4">
                    <div className="un-label text-un-grape mb-4">
                        {current?.tier === "pre" && "Pre-intake"}
                        {current?.tier === "t1" && "Tier 1: Core"}
                        {current?.tier === "gate" && "Depth amplifiers"}
                        {current?.tier === "t2" && "Tier 2: Depth"}
                        {current?.tier === "review" && "Ready to generate"}
                    </div>
                    <div className="un-h3 text-lg text-un-onyx/70 leading-relaxed max-w-md">
                        {current?.tier === "pre" && "Two quick foundation questions before we go deep."}
                        {current?.tier === "t1" && "Required questions that anchor the script's authority."}
                        {current?.tier === "gate" && "Optional. Adding these substantially improves depth."}
                        {current?.tier === "t2" && "Optional depth. Every answer here strengthens the script."}
                        {current?.tier === "review" && "Review answers, then generate a full spoken monologue."}
                    </div>
                    <div className="un-divider mt-8 pt-6">
                        <div className="un-mono text-xs text-un-onyx/50">
                            {saving ? "Saving…" : "Auto-saved"}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-8">
                    <div className="un-card p-8 md:p-10 animate-slide-up" key={step} data-testid="intake-step-card">
                        {current?.kind === "q" && <QuestionUI q={current.q} value={answers[current.q.key]} onChange={(v) => setAnswers((a) => ({ ...a, [current.q.key]: v }))} allAnswers={answers} inputRef={inputRef} projectId={id} />}

                        {current?.kind === "gate" && (
                            <div data-testid="intake-gate">
                                <div className="un-label text-un-grape mb-6">Optional deepening</div>
                                <h2 className="un-h1 text-3xl md:text-4xl mb-6">
                                    Want to answer a few optional questions to significantly deepen the quality of your scripts?
                                </h2>
                                <p className="text-un-onyx/70 mb-8 max-w-2xl">
                                    This is where the strategist earns its keep. Brand purpose, credibility,
                                    industry blind spots. Say yes if you have 3 more minutes.
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    <button
                                        onClick={() => setWantsTier2(true)}
                                        className={`un-btn-primary ${wantsTier2 === true ? "" : "opacity-90"}`}
                                        data-testid="gate-yes"
                                    >
                                        Yes, deepen it
                                    </button>
                                    <button
                                        onClick={() => setWantsTier2(false)}
                                        className={`un-btn-outline ${wantsTier2 === false ? "bg-un-onyx text-white" : ""}`}
                                        data-testid="gate-no"
                                    >
                                        No, skip to generate
                                    </button>
                                </div>
                            </div>
                        )}

                        {current?.kind === "review" && (
                            <div data-testid="intake-review">
                                <div className="un-label text-un-grape mb-4">Review</div>
                                <h2 className="un-h1 text-3xl md:text-4xl mb-4">
                                    Everything looks good. <span className="text-un-berry">Ready to generate?</span>
                                </h2>
                                <p className="text-un-onyx/70 mb-8 max-w-2xl">
                                    You&apos;ll get <span className="font-bold text-un-onyx">three distinct variants</span> (A, B, and C), each auto-saved as its own version. You can regenerate later, or edit these answers first.
                                </p>
                                <div className="grid grid-cols-1 gap-4 mb-8 max-h-[40vh] overflow-y-auto pr-2">
                                    {[...(schema?.pre_intake || []), ...(schema?.tier1 || []), ...(wantsTier2 ? schema?.tier2 || [] : [])].map((q) => (
                                        <div key={q.key} className="border-2 border-un-onyx p-4" data-testid={`review-${q.key}`}>
                                            <div className="un-label text-un-grape mb-2">{q.question}</div>
                                            <div className="text-un-onyx whitespace-pre-wrap break-words">
                                                {(() => {
                                                    const a = answers[q.key];
                                                    if (Array.isArray(a) && a.length) return a.join(", ");
                                                    return a ? String(a) : <span className="text-un-onyx/40">— not answered —</span>;
                                                })()}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    onClick={doGenerate}
                                    disabled={generating}
                                    className="un-btn-primary text-base flex items-center gap-2"
                                    data-testid="intake-generate-button"
                                >
                                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                    {generating ? "Writing 3 variants…" : "Generate 3 script variants"}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Nav */}
                    <div className="flex items-center justify-between gap-3 mt-6">
                        <button
                            onClick={back}
                            disabled={step === 0}
                            className="un-btn-outline text-sm flex items-center gap-2 disabled:opacity-40"
                            data-testid="intake-back-button"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                        {current?.kind !== "review" && (
                            <button
                                onClick={next}
                                className="un-btn-primary text-sm flex items-center gap-2"
                                data-testid="intake-next-button"
                            >
                                Continue <ArrowRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Fields that support the AI assistant. Kept in sync with backend FIELD_TARGETS.
// Question schemas can also opt in with `assist: true`.
const AI_ASSIST_KEYS = new Set([
    // Avatar / background fields
    "name_title", "communication_style", "offer_and_price", "ideal_buyer",
    "buyer_objections", "differentiator", "desired_cta", "why_brand_exists",
    "credibility", "who_for_who_not", "industry_gets_wrong", "why_message_works_now",
    "contrarian_belief", "signature_phrase", "brand_words", "creator_bio",
    // Per-video brief fields
    "video_topic", "video_pov", "video_myth", "video_data",
    "video_story", "video_belief_shift",
]);

const QuestionUI = ({ q, value, onChange, allAnswers, inputRef, projectId }) => {
    const supportsAi = q.assist === true || AI_ASSIST_KEYS.has(q.key);
    return (
        <div>
            <div className="un-label text-un-grape mb-4">Question · {q.key.replace(/_/g, " ")}</div>
            <h2 className="un-h1 text-3xl md:text-5xl mb-4 leading-tight" data-testid="intake-question-title">
                {q.question}
            </h2>
            {q.helper && (
                <p className="text-base md:text-lg text-un-onyx/70 mb-8 max-w-2xl leading-relaxed" data-testid="intake-question-helper">
                    {q.helper}
                </p>
            )}
            {q.type === "text" && (
                <div>
                    <input
                        ref={inputRef}
                        type="text"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        className="un-input text-lg"
                        placeholder="Type or dictate your answer"
                        data-testid={`intake-input-${q.key}`}
                    />
                    <div className="mt-3 flex justify-end gap-2 flex-wrap">
                        {supportsAi && (
                            <AiAssistButton
                                fieldKey={q.key}
                                fieldLabel={q.question}
                                currentFields={allAnswers || {}}
                                currentValue={value || ""}
                                onApply={(t) => onChange(t)}
                                projectId={projectId}
                            />
                        )}
                        <MicButton onAppend={(t) => onChange(((value || "") + (value ? " " : "") + t).trim())} label="Dictate" />
                    </div>
                </div>
            )}
            {q.type === "textarea" && (
                <div>
                    <textarea
                        ref={inputRef}
                        rows={6}
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        className="un-input text-base"
                        placeholder="Type or dictate your answer here…"
                        data-testid={`intake-input-${q.key}`}
                    />
                    <div className="mt-3 flex justify-end gap-2 flex-wrap">
                        {supportsAi && (
                            <AiAssistButton
                                fieldKey={q.key}
                                fieldLabel={q.question}
                                currentFields={allAnswers || {}}
                                currentValue={value || ""}
                                onApply={(t) => onChange(t)}
                                projectId={projectId}
                            />
                        )}
                        <MicButton
                            onAppend={(t) => onChange(((value || "") + (value ? " " : "") + t).trim())}
                            label="Dictate Answer"
                        />
                    </div>
                </div>
            )}
            {q.type === "choice_or_text" && (
                <div className="space-y-3" data-testid={`intake-choices-${q.key}`}>
                    {q.options.map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            onClick={() => onChange(opt)}
                            className={`w-full text-left border-2 border-un-onyx px-4 py-3 font-semibold transition-colors ${
                                value === opt ? "bg-un-berry text-white" : "bg-white text-un-onyx hover:bg-un-onyx hover:text-white"
                            }`}
                            data-testid={`intake-choice-${q.key}-${opt.slice(0, 8).replace(/\s+/g, "_")}`}
                        >
                            {opt}
                        </button>
                    ))}
                    <div className="pt-2">
                        <div className="un-label text-un-grape mb-2">Or describe in your own words</div>
                        <input
                            ref={inputRef}
                            type="text"
                            value={q.options.includes(value) ? "" : value || ""}
                            onChange={(e) => onChange(e.target.value)}
                            className="un-input"
                            placeholder="Custom style…"
                            data-testid={`intake-custom-${q.key}`}
                        />
                    </div>
                </div>
            )}
            {q.type === "duration" && (
                <div className="grid grid-cols-2 gap-3" data-testid={`intake-duration-${q.key}`}>
                    {q.options.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => onChange(opt.value)}
                            className={`text-left border-2 border-un-onyx px-4 py-4 font-bold transition-colors ${
                                value === opt.value ? "bg-un-berry text-white" : "bg-white text-un-onyx hover:bg-un-onyx hover:text-white"
                            }`}
                            data-testid={`intake-duration-choice-${opt.value}`}
                        >
                            <div className="un-mono text-xs opacity-70 mb-1">DURATION</div>
                            <div className="text-2xl">{opt.label}</div>
                        </button>
                    ))}
                </div>
            )}
            {q.type === "multi_choice" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`intake-multi-${q.key}`}>
                    {q.options.map((opt) => {
                        const arr = Array.isArray(value) ? value : [];
                        const on = arr.includes(opt);
                        return (
                            <button
                                key={opt}
                                type="button"
                                onClick={() => {
                                    const next = on ? arr.filter((x) => x !== opt) : [...arr, opt];
                                    onChange(next);
                                }}
                                className={`text-left border-2 border-un-onyx px-4 py-3 font-semibold flex items-center gap-3 transition-colors ${
                                    on ? "bg-un-berry text-white" : "bg-white text-un-onyx hover:bg-un-onyx hover:text-white"
                                }`}
                                data-testid={`intake-multi-choice-${q.key}-${opt.slice(0, 8).replace(/\s+/g, "_")}`}
                            >
                                <span className={`w-5 h-5 border-2 flex items-center justify-center shrink-0 ${on ? "border-white bg-white text-un-berry" : "border-un-onyx"}`}>
                                    {on ? "✓" : ""}
                                </span>
                                {opt}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default IntakeWizard;
